import React, { useMemo, useEffect, useRef } from 'react';
import { View, Text, Animated, Easing } from 'react-native';
import { Zap } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../../../src/theme/useTheme';
import {
  getChallengeCardStatusBannerStyles,
  getYourMoveTintGradient,
  getLiveTintGradient,
  getYourMoveAccentColor,
  getLiveAccentColor,
} from './ChallengeCardStatusBanner.styles';

interface ChallengeCardStatusBannerProps {
  variant: 'yourMove' | 'live';
}

export const ChallengeCardStatusBanner: React.FC<ChallengeCardStatusBannerProps> = ({
  variant,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getChallengeCardStatusBannerStyles(theme), [theme]);
  const isLive = variant === 'live';
  const tintColors = isLive ? getLiveTintGradient(theme) : getYourMoveTintGradient(theme);
  const accentColor = isLive ? getLiveAccentColor(theme) : getYourMoveAccentColor(theme);

  const pulse = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!isLive) return undefined;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 900,
          easing: Easing.out(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0,
          duration: 900,
          easing: Easing.in(Easing.quad),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [isLive, pulse]);

  const haloStyle = useMemo(
    () => ({
      transform: [
        {
          scale: pulse.interpolate({ inputRange: [0, 1], outputRange: [0.9, 1.6] }),
        },
      ],
      opacity: pulse.interpolate({ inputRange: [0, 1], outputRange: [0.35, 0] }),
    }),
    [pulse],
  );

  return (
    <View style={styles.bannerBleed}>
      <LinearGradient
        colors={tintColors}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={styles.tintFill}
      />
      <View style={[styles.accentRail, { backgroundColor: accentColor }]} />
      <View style={styles.bannerContent}>
        {isLive ? (
          <>
            <View style={styles.liveDotWrap}>
              <Animated.View style={[styles.liveDotHalo, haloStyle]} />
              <View style={styles.liveDot} />
            </View>
            <Text style={styles.bannerTextLive}>Live Now</Text>
          </>
        ) : (
          <>
            <Zap size={11} color={accentColor} fill={accentColor} />
            <Text style={styles.bannerTextYourMove}>Your Picks Needed</Text>
          </>
        )}
      </View>
    </View>
  );
};
